import { Form, Formik, useFormikContext } from "formik";
import { HourBillingButton } from "./HourBillingDialog";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { createHourAsync, updateHourAsync } from "../../../slices/hour";
import { PMField } from "../../utils/PMField";
import { PMDatePicker } from "../../utils/PMDatePicker";
import { fetchHour, fetchHourRegistrationPolicy, fetchHours } from "../../../api/hour";
import { fetchHolidays, holidaysByEffectiveDate } from "../../../api/holiday";
import { uploadModuleDocuments } from "../../../api/moduleDocumentUpload";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { ResourceLoading } from "../../utils/ResourceLoading";
import CarAllowanceSelectionModal from "../driving_record/carAllowanceSelectionModal";
import PropTypes from "prop-types";
import { dispatchWithToastV2 } from "../../Utils";
import { ReportSelector } from "../../utils/moduleSelectors/reportSelector";
import { ProjectPickerSheet, EmployeePickerSheet, ActivityPickerSheet } from "./hourFormPickers";
import { useParams } from "react-router-dom";
import { useUserHasAccess, useCanConfirmAgreedOvertime } from "../../../routes/roleOnly";
import { fetchDrivingTrips } from "../../../api/driving/trip";
import { ApproveHourDialog } from "./approveHourDialog";
import { AgreedOvertimeBadge } from "./AgreedOvertimeBadge";
import { HistoryModuleView } from "../histories/module-view";
import axios from "../../../api/axios";
import { checkAbsenceForDate } from "../../../api/absence/absenece";
import { fetchUserAbsences } from "../../../api/user";
import { PMNotice } from "../../utils/notifications";
import { DocumentManagerV6 } from "../Media/DocumentManagerV6";
import {
  expandAbsenceDateKeys,
  getEmploymentEndDate,
  isDateAfterEmploymentEnd,
} from "../../utils/employmentEndDate";

// Helper functions to convert between decimal hours and hours/minutes
const decimalToHoursMinutes = (decimalHours) => {
  if (!decimalHours && decimalHours !== 0) return { hours: 0, minutes: 0 };
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return { hours, minutes };
};

const hoursMinutesToDecimal = (hours, minutes) => {
  const h = parseFloat(hours) || 0;
  const m = parseFloat(minutes) || 0;
  return h + (m / 60);
};

const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = String(dateStr).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

// ── New "Promang" design tokens & helpers ───────────────────────────────────
const ACCENT = {
  solid: "#1463C7",
  grad: "linear-gradient(135deg,#1463C7,#2D8BE8)",
  deep: "#0B3A78",
  mid: "#2D8BE8",
  heroBg: "linear-gradient(160deg,#EAF3FD,#F2F8FE)",
  heroBorder: "#BFDCF7",
  soft: "#EAF3FD",
  glow: "rgba(20,99,199,",
};

const AVATAR_COLORS = ["#4F46E5", "#0D9488", "#D946EF", "#F59E0B", "#0EA5E9", "#10B981", "#1463C7"];
const avatarColor = (key) => {
  const s = String(key ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayISODate = () => toISODate(new Date());
const yesterdayISODate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toISODate(d);
};
const formatLongDateNb = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${String(dateStr).substring(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const s = d.toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const formatBytes = (b) => {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${Math.round(b / 1024)} KB`;
  return `${(b / 1048576).toFixed(1).replace(".", ",")} MB`;
};

// Shared design style fragments
const dsLabelStyle = { display: "block", fontSize: 13, fontWeight: 700, color: "#334155", margin: "0 0 9px" };
const dsFieldCardStyle = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 16px",
  border: "1.5px solid #E2E8F0",
  borderRadius: 14,
  background: "#fff",
  fontFamily: "inherit",
  textAlign: "left",
};
const dsPillStyle = (active) =>
  active
    ? {
        padding: "9px 15px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: "inherit",
        border: "1.5px solid transparent",
        color: "#fff",
        background: ACCENT.grad,
        boxShadow: `0 6px 14px -5px ${ACCENT.glow}.6)`,
      }
    : {
        padding: "9px 15px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: "inherit",
        border: "1.5px solid #E2E8F0",
        background: "#fff",
        color: "#475569",
      };
const dsTabStyle = (active) => {
  const base = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    padding: 10,
    borderRadius: 11,
    border: "none",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  };
  return active
    ? { ...base, color: "#fff", background: ACCENT.grad, boxShadow: `0 6px 14px -6px ${ACCENT.glow}.55)` }
    : { ...base, background: "#F1F5F9", color: "#64748B" };
};
const DsChevron = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#94A3B8"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const hourLinkedToReport = (reportId) => {
  if (reportId === null || reportId === undefined || reportId === "") return false;
  const n = Number(reportId);
  if (Number.isNaN(n)) return true;
  return n !== 0;
};

const HOUR_PENDING_DOC_MIME_WHITELIST = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/svg",
  "image/svg+xml",
  "image/webp",
  "application/pdf",
]);

const pendingDocAcceptAttr =
  "image/png, image/jpeg, image/jpg, image/gif, image/svg, image/svg+xml, image/webp, application/pdf";

function isHourPendingDocMimeAllowed(file) {
  const t = (file?.type || "").toLowerCase().trim();
  return t && HOUR_PENDING_DOC_MIME_WHITELIST.has(t);
}

/** RTK `dispatchWithToastV2` passes the fulfilled action.payload; unwrap nested shapes if present. */
function normalizeHourThunkPayload(savedPayload) {
  if (!savedPayload || typeof savedPayload !== "object") return savedPayload;
  if (savedPayload.data && typeof savedPayload.data === "object" && savedPayload.data.id != null) {
    return savedPayload.data;
  }
  if (savedPayload.hour && typeof savedPayload.hour === "object" && savedPayload.hour.id != null) {
    return savedPayload.hour;
  }
  return savedPayload;
}

function coercePositiveHourDbId(raw) {
  if (raw == null || raw === "") return null;
  const n =
    typeof raw === "number" && Number.isFinite(raw)
      ? Math.trunc(raw)
      : parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

// Observe form date changes and notify parent + internal state
const FormikDateObserver = ({ onDateChange, onInternalDate }) => {
  const { values } = useFormikContext();
  const prevRef = useRef(values?.date);
  useEffect(() => {
    const d = values?.date;
    if (d && d !== prevRef.current) {
      prevRef.current = d;
      if (onDateChange) onDateChange(d);
      if (onInternalDate) onInternalDate(d);
    }
  }, [values?.date]);
  return null;
};

// Observe hours/minutes changes and sync with time field
// Also observe time field changes and sync back to hours/minutes
// Only active when using hours_minutes format
const HoursMinutesObserver = ({ useHoursMinutesFormat }) => {
  const { values, setFieldValue } = useFormikContext();
  const prevHoursRef = useRef(values?.hours);
  const prevMinutesRef = useRef(values?.minutes);
  const prevTimeRef = useRef(values?.time);
  
  // Sync hours/minutes -> time (only when both are valid integers, not during decimal input)
  useEffect(() => {
    // Only observe if using hours_minutes format
    if (!useHoursMinutesFormat) {
      return;
    }
    
    const hoursStr = values?.hours;
    const minutesStr = values?.minutes;
    
    // Check if hours contains a decimal point (user typing 2.5 or 2,5)
    const hoursIsDecimal = hoursStr && (hoursStr.toString().includes('.') || hoursStr.toString().includes(','));
    
    // Only sync if hours is NOT a decimal (i.e., user is typing integer hours)
    if (!hoursIsDecimal && (hoursStr !== prevHoursRef.current || minutesStr !== prevMinutesRef.current) && 
        (hoursStr !== undefined && minutesStr !== undefined)) {
      const hoursNum = parseFloat(hoursStr?.toString().replace(',', '.') || '0');
      const minutesNum = parseFloat(minutesStr?.toString().replace(',', '.') || '0');
      
      prevHoursRef.current = hoursStr;
      prevMinutesRef.current = minutesStr;
      
      // Use integer values for calculation
      const hoursInt = Math.floor(hoursNum);
      const minutesInt = Math.round(minutesNum);
      const decimalTime = hoursMinutesToDecimal(hoursInt, minutesInt);
      const timeStr = decimalTime.toFixed(3);
      if (timeStr !== prevTimeRef.current) {
        prevTimeRef.current = timeStr;
        setFieldValue("time", timeStr, false); // false = don't validate
      }
    } else if (hoursIsDecimal) {
      // If user is typing a decimal, just update the time field without converting hours/minutes
      const hoursNum = parseFloat(hoursStr?.toString().replace(',', '.') || '0');
      if (!isNaN(hoursNum) && hoursNum >= 0) {
        const timeStr = hoursNum.toFixed(3);
        if (timeStr !== prevTimeRef.current) {
          prevTimeRef.current = timeStr;
          setFieldValue("time", timeStr, false);
        }
      }
    }
  }, [values?.hours, values?.minutes, setFieldValue, useHoursMinutesFormat]);
  
  // Sync time -> hours/minutes (when time is updated externally, but not if hours is currently a decimal)
  useEffect(() => {
    // Only observe if using hours_minutes format
    if (!useHoursMinutesFormat) {
      return;
    }
    
    const timeValue = values?.time;
    const hoursStr = values?.hours;
    const hoursIsDecimal = hoursStr && (hoursStr.toString().includes('.') || hoursStr.toString().includes(','));
    
    // Only sync back if time changed externally AND user is not currently typing a decimal
    if (!hoursIsDecimal && timeValue !== undefined && timeValue !== prevTimeRef.current && timeValue !== "") {
      prevTimeRef.current = timeValue;
      const decimalTime = parseFloat(timeValue);
      if (!isNaN(decimalTime)) {
        const { hours, minutes } = decimalToHoursMinutes(decimalTime);
        const hoursStr = hours.toString();
        const minutesStr = minutes.toString();
        if (hoursStr !== prevHoursRef.current || minutesStr !== prevMinutesRef.current) {
          prevHoursRef.current = hoursStr;
          prevMinutesRef.current = minutesStr;
          setFieldValue("hours", hoursStr, false);
          setFieldValue("minutes", minutesStr, false);
        }
      }
    }
  }, [values?.time, values?.hours, setFieldValue, useHoursMinutesFormat]);
  
  return null;
};


export const HourForm = ({
  hourId,
  onChange,
  prefill = null,
  onDateChange = null,
  renderAfterDateTime = null,
  renderAfterDescription = null,
  onClose = null,
  showQuickHourSelection = false,
  hideProjectSelector = false,
  hideDescription = false,
  /** When true, hide auto-selected project on new hours (e.g. report context). Editing always shows project. */
  hideAutoSelectedProject = false,
  onWarningStateChange = null,
  showPrefilledContext = false,
}) => {
  const [fetchingHour, setFetchingHour] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { id } = useParams();
  const [resolvedId, setResolvedId] = useState(null);
  const currentUser = useSelector((state) => state.session.currentUser);
  // Get time registration format from user settings, default to "hours_minutes"
  const timeRegistrationFormat = currentUser?.time_registration_format || "hours_minutes";
  const useHoursMinutesFormat = timeRegistrationFormat === "hours_minutes";

  const [item, setItem] = useState({});
  const isAdmin = useUserHasAccess({ role: "hours", action: "admin" });
  const canCarAllowance = useUserHasAccess({ role: "driving_trips" });
  const canConfirmOt = useCanConfirmAgreedOvertime();
  /** Matches API: only company admin may process hours (hour[process]=true) */
  const canProcessHour = useMemo(
    () => Boolean(currentUser?.roles?.some((r) => r.name === "admin")),
    [currentUser?.roles]
  );
  const [hasTripsAvailable, setHasTripsAvailable] = useState(false);
  const [formDate, setFormDate] = useState(prefill?.date || null);
  const [datePickerDayInfo, setDatePickerDayInfo] = useState({});
  const [absenceDateKeys, setAbsenceDateKeys] = useState(() => new Set());
  const [employmentEndByUser, setEmploymentEndByUser] = useState({});
  const [holidayByDate, setHolidayByDate] = useState(() => new Map());
  const [registrationPolicy, setRegistrationPolicy] = useState(null);
  const agreedOvertimeEnabled = Boolean(registrationPolicy?.agreed_overtime_enabled);
  const [pendingHourDocuments, setPendingHourDocuments] = useState([]);
  const pendingHourDocInputRef = useRef(null);
  // Holds the last saved hour so onChange can be deferred until the user
  // dismisses the "Time lagret" overlay (parents close the dialog on onChange).
  const lastSavedHourRef = useRef(null);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [warningData, setWarningData] = useState(null);
  const [noticeErrors, setNoticeErrors] = useState(null);
  /** "details" | "history" — history as tab avoids stacking/clipping inside PMDialog */
  const [hourEditorTab, setHourEditorTab] = useState("details");

  // New design: "Time lagret" success overlay + controlled selector dialogs
  const [savedOverlay, setSavedOverlay] = useState(false);
  const [savedSummary, setSavedSummary] = useState(null);
  const [empPickerOpen, setEmpPickerOpen] = useState(false);
  const [projPickerOpen, setProjPickerOpen] = useState(false);
  const [actPickerOpen, setActPickerOpen] = useState(false);
  // New design: header tabs ("Ny timeføring" / "Registrerte timer") + day list
  const [mainView, setMainView] = useState("form");
  const [listDate, setListDate] = useState(() => new Date().toISOString().substring(0, 10));
  const [listEntries, setListEntries] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  // Update resolvedId whenever hourId or id changes
  // Prioritize hourId prop over id from useParams
  useEffect(() => {
    if (hourId !== undefined) {
      // hourId is explicitly provided (even if null)
      setResolvedId(hourId || null);
    } else {
      // hourId not provided, fall back to id from route
      setResolvedId(id || null);
    }
  }, [hourId, id]);

  useEffect(() => {
    setHourEditorTab("details");
  }, [resolvedId]);

  const isEditingExistingHour = Boolean(resolvedId);

  /** Prefilled AO/report/project context → hide selectors for normal users; admin may override */
  const { showProjectField, showActivityField, lockProjectField, lockActivityField } = useMemo(() => {
    if (isEditingExistingHour) {
      return {
        showProjectField: true,
        showActivityField: true,
        lockProjectField: false,
        lockActivityField: false,
      };
    }
    if (!prefill || typeof prefill !== "object") {
      return {
        showProjectField: true,
        showActivityField: true,
        lockProjectField: false,
        lockActivityField: false,
      };
    }
    const nonEmptyId = (v) =>
      v != null && String(v).trim() !== "";
    let lockProj = false;
    if (
      nonEmptyId(prefill.report_id) ||
      nonEmptyId(prefill.work_order_id) ||
      nonEmptyId(prefill.project_id) ||
      prefill.project?.id != null
    ) {
      lockProj = true;
    }
    let lockAct = false;
    if (
      nonEmptyId(prefill.project_activity_id) ||
      prefill.project_activity?.id != null
    ) {
      lockAct = true;
    }
    const canReveal = isAdmin || canProcessHour;
    if (hideAutoSelectedProject && lockProj) {
      return {
        showProjectField: false,
        showActivityField: !lockAct || canReveal || showPrefilledContext,
        lockProjectField: false,
        lockActivityField: lockAct && showPrefilledContext && !canReveal,
      };
    }
    return {
      showProjectField: !lockProj || canReveal || showPrefilledContext,
      showActivityField: !lockAct || canReveal || showPrefilledContext,
      lockProjectField: lockProj && showPrefilledContext && !canReveal,
      lockActivityField: lockAct && showPrefilledContext && !canReveal,
    };
  }, [isEditingExistingHour, prefill, isAdmin, canProcessHour, showPrefilledContext, hideAutoSelectedProject]);

  const hasProjectFieldError = noticeErrors?.some((e) => e.field === "project_id");
  const hasActivityFieldError = noticeErrors?.some((e) => e.field === "project_activity_id");
  const showProjectSelectorUi = (showProjectField || hasProjectFieldError) && !hideProjectSelector;
  const showProjectActivityUi = showActivityField || hasActivityFieldError;

  const createOrUpdateDispatch = (data) => {
    if (item?.id) {
      const res = dispatch(updateHourAsync(data));
      return res;
    } else {
      const res = dispatch(createHourAsync(data));
      return res;
    }
  };
  
  const submitHour = (values, helpers) => {
    try {
      // Clear previous notice errors
      setNoticeErrors(null);
      setIsSaving(true);

      // Remove hours and minutes from payload - only send time (decimal format) to backend
      const { hours, minutes, project_activity, activity, project, ...payload } = values;
      if (agreedOvertimeEnabled) {
        const requested = Boolean(values.agreed_overtime_requested);
        payload.agreed_overtime_requested = requested;
        if (requested) {
          const otHours = Number.parseFloat(String(values.agreed_overtime_hours).replace(",", "."));
          payload.agreed_overtime_hours = Number.isFinite(otHours) ? otHours : null;
        } else {
          payload.agreed_overtime_hours = null;
        }
      } else {
        delete payload.agreed_overtime_requested;
        delete payload.agreed_overtime_hours;
      }
      dispatchWithToastV2(createOrUpdateDispatch(payload), {
        // The form shows its own "Time lagret" screen, so no success toast.
        suppressSuccessToast: true,
        savingText: t("Lagrer…"),
        onRefresh: (thunkSavedPayload) => {
          const savedHour = normalizeHourThunkPayload(thunkSavedPayload);
          const hourNumericId = coercePositiveHourDbId(savedHour?.id ?? thunkSavedPayload?.id);

          const completeHourSaveSuccess = () => {
            // Defer onChange until the overlay is dismissed — parents close the
            // dialog on onChange, which would unmount the "Time lagret" screen.
            lastSavedHourRef.current = savedHour;
            setIsSaving(false);
            setNoticeErrors(null); // Clear errors on success
            helpers.setStatus({ success: true });
            helpers.setSubmitting(false);
            // Show the "Time lagret" success overlay (design); the user picks
            // "Registrer ny" or "Lukk" from there instead of auto-closing.
            const projObj = values.project || item?.project || prefill?.project || null;
            const { hours: sH, minutes: sM } = decimalToHoursMinutes(parseFloat(values.time) || 0);
            setSavedSummary({
              durationLabel: `${sH}t ${pad2(sM)}m`,
              projectLabel: projObj?.displayTitle || projObj?.name || "—",
              dateLabel: formatLongDateNb(values.date),
            });
            setSavedOverlay(true);
          };

          if (pendingHourDocuments.length > 0) {
            if (!hourNumericId) {
              console.error("[HourForm] Pending documents but missing hour id in save response", thunkSavedPayload);
              toast.error(t("hour.time_documents.pending_upload_failed"));
              completeHourSaveSuccess();
              return;
            }
            const filesToUpload = pendingHourDocuments.map((p) => p.file);
            setIsSaving(true);
            uploadModuleDocuments("hour", hourNumericId, filesToUpload)
              .then(() => {
                setPendingHourDocuments([]);
                toast.success(
                  t("hour.time_documents.pending_upload_done", { count: filesToUpload.length })
                );
              })
              .catch((err) => {
                console.error(err);
                toast.error(t("hour.time_documents.pending_upload_failed"));
              })
              .finally(() => {
                completeHourSaveSuccess();
              });
            return;
          }

          completeHourSaveSuccess();
        },
        onError: (errorPayload) => {
          setIsSaving(false);
          
          // Handle notice messages from backend - check multiple possible locations
          let errors = null;
          
          // Check if errors are directly in errorPayload (from success callback with errors)
          if (errorPayload?.errors && typeof errorPayload.errors === "object") {
            errors = errorPayload.errors;
          }
          // Check if errors are in errorPayload.response.data.errors (from axios error in error callback)
          else if (errorPayload?.response?.data?.errors && typeof errorPayload.response.data.errors === "object") {
            errors = errorPayload.response.data.errors;
          }
          // Check if errorPayload.response.data itself is the errors object
          else if (errorPayload?.response?.data && typeof errorPayload.response.data === "object") {
            const data = errorPayload.response.data;
            // Check if it looks like an errors object (has field names as keys with array/string values)
            const keys = Object.keys(data);
            if (keys.length > 0 && keys.every(key => 
              Array.isArray(data[key]) || typeof data[key] === "string"
            )) {
              errors = data;
            }
          }
          // Check if errorPayload itself is the errors object (direct from setErrors)
          else if (errorPayload && typeof errorPayload === "object" && !errorPayload.response && !errorPayload.message) {
            // Check if it looks like an errors object (has field names as keys)
            const keys = Object.keys(errorPayload);
            if (keys.length > 0 && keys.every(key => 
              Array.isArray(errorPayload[key]) || typeof errorPayload[key] === "string"
            )) {
              errors = errorPayload;
            }
          }
          
          if (errors) {
            const fieldErrors = {};
            const noticeMessages = [];
            
            Object.entries(errors).forEach(([field, messages]) => {
              if (Array.isArray(messages) && messages.length > 0) {
                // Use the first message for the field
                const errorMessage = messages[0];
                fieldErrors[field] = errorMessage;
                // Collect notice messages for display
                noticeMessages.push({
                  field: field,
                  message: errorMessage
                });
              } else if (typeof messages === "string") {
                fieldErrors[field] = messages;
                noticeMessages.push({
                  field: field,
                  message: messages
                });
              }
            });
            
            // Set Formik field errors first
            if (Object.keys(fieldErrors).length > 0) {
              helpers.setErrors(fieldErrors);
            }
            
            // Store notice errors for display
            if (noticeMessages.length > 0) {
              setNoticeErrors(noticeMessages);
            }
          }
          
          helpers.setStatus({ success: false });
          helpers.setSubmitting(false);
          // Do NOT close dialog on error - let user see the errors
        },
        setErrors: (errors) => {
          // Handle errors from dispatchWithToastV2
          // This is called before onError, so we handle errors here first
          if (errors && typeof errors === "object") {
            const fieldErrors = {};
            const noticeMessages = [];
            
            Object.entries(errors).forEach(([field, messages]) => {
              if (Array.isArray(messages) && messages.length > 0) {
                // Use the first message for the field
                const errorMessage = messages[0];
                fieldErrors[field] = errorMessage;
                noticeMessages.push({
                  field: field,
                  message: errorMessage
                });
              } else if (typeof messages === "string") {
                fieldErrors[field] = messages;
                noticeMessages.push({
                  field: field,
                  message: messages
                });
              }
            });
            
            // Set Formik field errors
            if (Object.keys(fieldErrors).length > 0) {
              helpers.setErrors(fieldErrors);
            }
            
            // Store notice errors for display
            if (noticeMessages.length > 0) {
              setNoticeErrors(noticeMessages);
            }
            
            // Also update saving and submitting state
            setIsSaving(false);
            helpers.setStatus({ success: false });
            helpers.setSubmitting(false);
          }
        },
      });
    } catch (err) {
      setIsSaving(false);
      toast.error("Something went wrong...");
      console.error(err);
      helpers.setStatus({ success: false });
      helpers.setSubmitting(false);
      // Do NOT close dialog on error
    }
  };
  
  const handleWarningConfirm = () => {
    if (pendingSubmission) {
      submitHour(pendingSubmission.values, pendingSubmission.helpers);
      setPendingSubmission(null);
      setWarningData(null);
      setWarningDialogOpen(false);
    }
  };
  
  // Expose methods to parent for warning dialog control
  useEffect(() => {
    if (typeof onWarningStateChange === 'function') {
      onWarningStateChange({
        warningDialogOpen,
        warningData,
        pendingSubmission,
        setWarningDialogOpen,
        setWarningData,
        setPendingSubmission,
        handleWarningConfirm,
      });
    }
  }, [warningDialogOpen, warningData, pendingSubmission, onWarningStateChange]);

  const getItem = async (itemId) => {
    setFetchingHour(true);
    fetchHour(itemId)
      .then((res) => {
        const d = res.data;
        if (d && typeof d === "object") {
          const { activity: _legacyActivity, ...rest } = d;
          setItem(rest);
        } else {
          setItem(d);
        }
        setFetchingHour(false);
      })
      .catch((err) => {
        console.error(err);
        setFetchingHour(false);
      });
  };

  // Fetch item when resolvedId changes
  useEffect(() => {
    if (resolvedId) {
      getItem(resolvedId);
      setNoticeErrors(null); // Clear notice errors when loading a new hour
    } else {
      // Clear item state when creating new hour
      setItem({});
      setFetchingHour(false);
      setNoticeErrors(null); // Clear notice errors when creating new hour
    }
  }, [resolvedId]);

  const prefillIdentity = useMemo(() => {
    if (!prefill || typeof prefill !== "object") return null;
    return [
      prefill.date,
      prefill.report_id,
      prefill.work_order_id,
      prefill.project_id,
      prefill.project_activity_id,
    ]
      .map((v) => (v == null ? "" : String(v)))
      .join("|");
  }, [prefill]);

  useEffect(() => {
    if (resolvedId || !prefillIdentity) return;
    setItem({});
    setNoticeErrors(null);
  }, [prefillIdentity, resolvedId]);

  useEffect(() => {
    let active = true;
    fetchHourRegistrationPolicy()
      .then((res) => {
        if (!active) return;
        setRegistrationPolicy(res?.data?.data || null);
      })
      .catch(() => {
        if (active) setRegistrationPolicy(null);
      });
    return () => {
      active = false;
    };
  }, []);

  // Fetch the selected day's registered hours for the "Registrerte timer" tab.
  useEffect(() => {
    if (mainView !== "list") return undefined;
    let active = true;
    setListLoading(true);
    const uid = currentUser?.id;
    const filters = isAdmin || canProcessHour ? [] : uid ? [{ name: "user", value: uid }] : [];
    fetchHours({ date_from: listDate, date_to: listDate, per_page: 200, page: 1, filters })
      .then((res) => {
        if (active) setListEntries(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (active) setListEntries([]);
      })
      .finally(() => {
        if (active) setListLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mainView, listDate, currentUser?.id, isAdmin, canProcessHour]);

  // Clear item when hourId becomes null (switching to new hour)
  useEffect(() => {
    // If hourId is explicitly null, clear the form
    if (hourId === null) {
      setItem({});
      setResolvedId(null);
      setPendingHourDocuments([]);
      // Reset formDate to prefill date or null
      setFormDate(prefill?.date || null);
      setNoticeErrors(null); // Clear notice errors when switching to new hour
    }
  }, [hourId, prefill?.date]);

  // Check trips availability for car allowance when date or user changes
  useEffect(() => {
    let active = true;
    async function checkTrips() {
      if (!formDate || !currentUser?.id) {
        if (active) setHasTripsAvailable(false);
        return;
      }
      try {
        const res = await fetchDrivingTrips({
          filters: [
            { name: "USER", value: currentUser.id },
            { name: "DATE", value: formDate },
          ],
          per_page: 1,
          page: 1,
        });
        const list = Array.isArray(res?.data) ? res.data : [];
        if (active) setHasTripsAvailable(list.length > 0);
      } catch (e) {
        if (active) setHasTripsAvailable(false);
      }
    }
    checkTrips();
    return () => {
      active = false;
    };
  }, [formDate, currentUser?.id]);

  const fetchHolidaysForMonth = async (month, year) => {
    const lastDay = new Date(year, month, 0).getDate();
    const dateFrom = `${year}-${String(month).padStart(2, "0")}-01`;
    const dateTo = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    try {
      const res = await fetchHolidays({ date_from: dateFrom, date_to: dateTo });
      return holidaysByEffectiveDate(res?.data || []);
    } catch (e) {
      console.error("Error fetching holidays for date picker:", e);
      return new Map();
    }
  };

  useEffect(() => {
    if (!formDate) return undefined;

    let active = true;
    const parsed = parseLocalDate(formDate);
    if (!parsed) return undefined;

    fetchHolidaysForMonth(parsed.getMonth() + 1, parsed.getFullYear()).then((monthHolidays) => {
      if (!active || monthHolidays.size === 0) return;
      setHolidayByDate((prev) => {
        const next = new Map(prev);
        monthHolidays.forEach((holiday, dateKey) => next.set(dateKey, holiday));
        return next;
      });
    });

    return () => {
      active = false;
    };
  }, [formDate]);

  // Fetch hours per day for date picker (aggregate by date for dayInfoMap)
  const fetchHoursForMonth = async (month, year, userId) => {
    const uid = userId || currentUser?.id;
    if (!uid) return {};
    const lastDay = new Date(year, month, 0).getDate();
    const dateFrom = `${year}-${String(month).padStart(2, "0")}-01`;
    const dateTo = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    try {
      const res = await fetchHours({
        date_from: dateFrom,
        date_to: dateTo,
        per_page: 200,
        page: 1,
        filters: [{ name: "user", value: uid }],
      });
      const hours = res?.data || [];
      const byDate = {};
      hours.forEach((h) => {
        const d = h?.date;
        if (d) {
          byDate[d] = (byDate[d] || 0) + (parseFloat(h?.time) || 0);
        }
      });
      const dayInfoMap = {};
      Object.entries(byDate).forEach(([date, total]) => {
        const h = Math.floor(total);
        const m = Math.round((total - h) * 60);
        dayInfoMap[date] = m > 0 ? `${h}t ${m}m` : `${h}t`;
      });
      const monthHolidays = await fetchHolidaysForMonth(month, year);
      if (monthHolidays.size > 0) {
        setHolidayByDate((prev) => {
          const next = new Map(prev);
          monthHolidays.forEach((holiday, dateKey) => next.set(dateKey, holiday));
          return next;
        });
        monthHolidays.forEach((holiday, date) => {
          dayInfoMap[date] = dayInfoMap[date]
            ? `${dayInfoMap[date]} | ${holiday.name}`
            : holiday.name;
        });
      }
      const daysInMonth = new Date(year, month, 0).getDate();
      for (let day = 1; day <= daysInMonth; day += 1) {
        const date = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        if (isLockedByPolicy(date)) {
          dayInfoMap[date] = dayInfoMap[date] ? `${dayInfoMap[date]} | Låst` : "Låst";
        }
      }
      return dayInfoMap;
    } catch (e) {
      console.error("Error fetching hours for date picker:", e);
      return {};
    }
  };

  const isLockedByPolicy = (dateValue) => {
    if (canProcessHour) return false;
    if (!registrationPolicy?.payroll_lock_enabled) return false;

    const date = parseLocalDate(dateValue);
    if (!date) return false;

    const lockHour = Number.parseInt(registrationPolicy?.payroll_lock_hour, 10);
    const effectiveLockHour = Number.isNaN(lockHour) ? 18 : Math.min(23, Math.max(0, lockHour));
    const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0, effectiveLockHour, 0, 0, 0);

    return new Date() >= monthEnd;
  };

  // useEffect(() => {
  //   if (selected) {
  //     // Fetch hour and update state
  //     getItem(selected.id);
  //   } else {
  //     setLoading(false);
  //   }
  // }, [selected]);

  const handleEmptyForm = (formik) => {
    if (formik) {
      formik.resetForm();
    }
    setItem({});
    setResolvedId(null);
    setPendingHourDocuments([]);
    setNoticeErrors(null); // Clear notice errors when resetting form

    // remove only :id from the URL
    const newPath = window.location.pathname.replace(/\/\d+$/, "");
    window.history.pushState({}, "", newPath);
  };

  const [carAllowanceDialogOpen, setCarAllowanceDialogOpen] = useState(false);

  const formDisabled = (dateValue = null) => {
    if (item?.id && item?.approved && !canProcessHour) {
      return true;
    }
    if (isLockedByPolicy(dateValue || item?.date || formDate || prefill?.date)) {
      return true;
    }
    return false;
  };

  if (fetchingHour && resolvedId && !item?.id) return <ResourceLoading />;

  return (
    <>
      <Formik
        key={hourId || `new-${prefill?.report_id || prefill?.project_id || 'default'}`}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={{
          id: item?.id || null,
          user_id: item?.id 
            ? (item?.user?.id || currentUser?.id)
            : (prefill?.user_id || currentUser?.id),
          project_id: item?.id
            ? (item?.project?.id ? item.project.id.toString() : null)
            : (() => {
                const fromPrefill = prefill?.project_id ?? prefill?.project?.id;
                if (fromPrefill != null) return String(fromPrefill);
                if (item?.project?.id != null) return String(item.project.id);
                return null;
              })(),
          project: item?.id
            ? (item?.project || null)
            : (prefill?.project || item?.project || null),
          project_activity_id: item?.id
            ? item?.project_activity?.id != null
              ? String(item.project_activity.id)
              : item?.project_activity_id != null
                ? String(item.project_activity_id)
                : null
            : (() => {
                const fromPrefill =
                  prefill?.project_activity_id ?? prefill?.project_activity?.id;
                if (fromPrefill != null) return String(fromPrefill);
                if (item?.project_activity?.id != null) return String(item.project_activity.id);
                return null;
              })(),
          project_activity: item?.id
            ? item?.project_activity ||
                (item?.project_activity_id != null ? { id: item.project_activity_id } : null)
            : (prefill?.project_activity || item?.project_activity || null),
          time: item?.id
            ? (item?.time || "")
            : (prefill?.time || ""),
          // Convert time to hours and minutes for display (only if using hours_minutes format)
          ...(useHoursMinutesFormat ? (() => {
            const timeValue = item?.id
              ? (item?.time || "")
              : (prefill?.time || "");
            const decimalTime = parseFloat(timeValue) || 0;
            const { hours, minutes } = decimalToHoursMinutes(decimalTime);
            return { hours: hours.toString(), minutes: minutes.toString() };
          })() : { hours: "0", minutes: "0" }),
          date: item?.id
            ? (item?.date || new Date().toISOString().substring(0, 10))
            : (prefill?.date || new Date().toISOString().substring(0, 10)),
          report_id: item?.id
            ? (item?.report?.id || null)
            : (prefill?.report_id || null),
          work_order_id: item?.id
            ? (item?.work_order?.id || null)
            : (prefill?.work_order_id || null),
          description: item?.id
            ? (item?.description || "")
            : (prefill?.description || ""),
          agreed_overtime_requested: Boolean(item?.agreed_overtime_requested),
          agreed_overtime_hours: item?.id
            ? (item?.agreed_overtime_hours != null
                ? String(item.agreed_overtime_hours)
                : item?.time != null
                  ? String(item.time)
                  : "")
            : "",
          submit: null,
        }}
        validationSchema={Yup.object({
          date: Yup.date().required("Date is required!"),
          time: Yup.string().required("Time is required!"),
          agreed_overtime_hours: Yup.string().when("agreed_overtime_requested", {
            is: true,
            then: (schema) =>
              schema
                .required(t("hours.agreed_overtime.hours_required"))
                .test(
                  "ot-range",
                  t("hours.agreed_overtime.hours_invalid"),
                  function (value) {
                    const ot = Number.parseFloat(String(value || "").replace(",", "."));
                    const total = Number.parseFloat(String(this.parent.time || "").replace(",", "."));
                    if (!Number.isFinite(ot) || ot <= 0) return false;
                    if (!Number.isFinite(total)) return false;
                    return ot <= total + 0.001;
                  }
                ),
            otherwise: (schema) => schema.nullable(),
          }),
          // Only validate hours/minutes if using hours_minutes format
          ...(useHoursMinutesFormat ? {
            hours: Yup.string()
              .test('is-valid-number', 'Hours must be a valid number', (value) => {
                if (!value || value === '') return false;
                const num = parseFloat(value.replace(',', '.'));
                return !isNaN(num) && num >= 0;
              })
              .required("Hours is required!"),
            minutes: Yup.string()
              .test('is-valid-number', 'Minutes must be a valid number between 0 and 59', (value) => {
                if (!value || value === '') return false;
                const num = parseFloat(value.replace(',', '.'));
                return !isNaN(num) && num >= 0 && num <= 59;
              })
              .required("Minutes is required!"),
          } : {}),
          project_id: Yup.string()
            .nullable()
            .required("Project is required!"),
          // report_id: Yup.string().nullable().required("Report is required!"),
          // deviation_id: Yup.string().nullable().required("Deviation is required!"),
          project_activity_id: Yup.string()
            .nullable()
            .required("Aktivitet er påkrevd"),
        })}
        onSubmit={async (values, helpers) => {
          setIsSaving(true);
          try {
            if (isLockedByPolicy(values.date)) {
              setIsSaving(false);
              helpers.setSubmitting(false);
              setNoticeErrors([{ field: "date", message: t("Denne datoen er låst for timeregistrering.") }]);
              return;
            }

            // Check if there's an absence for this date
            if (values.user_id && values.date) {
              const endDate =
                employmentEndByUser[values.user_id] ||
                getEmploymentEndDate(item?.user) ||
                getEmploymentEndDate(currentUser);
              if (isDateAfterEmploymentEnd(values.date, { employment_ends: endDate })) {
                setIsSaving(false);
                toast.error(t("employment.end_date.hours_blocked", { date: endDate }));
                helpers.setSubmitting(false);
                return;
              }
              try {
                const hasAbsence = await checkAbsenceForDate(values.user_id, values.date);
                if (hasAbsence) {
                  setIsSaving(false);
                  toast.error(t("Cannot register hours on a day with absence registered"));
                  helpers.setSubmitting(false);
                  return;
                }
              } catch (err) {
                console.error("Error checking absence:", err);
                // Continue with submission if check fails
              }
            }

            // Check if we need to show warning for price line activity
            if (values.report_id && values.project_activity_id && values.time) {
              try {
                const reportResponse = await axios.get(`/service/reports/${values.report_id}`, {
                  params: { fields: "price_lines.activity_item,price_lines.project_activity" },
                });
                const report = reportResponse.data;

                const paRow = values.project_activity;
                const matchesAgreedLine = (pl) => {
                  if (!pl?.is_agreed_price_line) return false;
                  if (
                    values.project_activity_id &&
                    pl.project_activity_id != null &&
                    String(pl.project_activity_id) === String(values.project_activity_id)
                  ) {
                    return true;
                  }
                  if (
                    paRow?.activity_item_id &&
                    pl.activity_item_id != null &&
                    String(pl.activity_item_id) === String(paRow.activity_item_id)
                  ) {
                    return true;
                  }
                  return false;
                };

                const priceLine = report?.price_lines?.find(matchesAgreedLine);

                if (priceLine && priceLine.agreed_hours > 0) {
                  const hoursResponse = await axios.get(`/hours`, {
                    params: {
                      filters: JSON.stringify([
                        { name: "REPORT", value: values.report_id },
                        { name: "PROJECT_ACTIVITY", value: values.project_activity_id },
                      ]),
                    },
                  });
                  
                  const existingHours = hoursResponse.data?.data || [];
                  const currentTotalHours = existingHours.reduce((sum, h) => sum + (parseFloat(h.time) || 0), 0);
                  const newTime = parseFloat(values.time) || 0;
                  const totalAfterRegistration = currentTotalHours + newTime;
                  
                  // Check if total will exceed estimated hours
                  if (totalAfterRegistration > priceLine.agreed_hours) {
                    // Show warning dialog
                    setIsSaving(false);
                    setWarningData({
                      activityTitle:
                        priceLine.project_activity?.display_title ||
                        priceLine.project_activity?.title ||
                        priceLine.activity_item?.displayTitle ||
                        priceLine.title ||
                        t("hours.registration_warning.this_activity"),
                      estimatedHours: priceLine.agreed_hours,
                      registeredHours: currentTotalHours,
                      newHours: newTime,
                      totalAfterRegistration: totalAfterRegistration,
                    });
                    setPendingSubmission({ values, helpers });
                    setWarningDialogOpen(true);
                    helpers.setSubmitting(false);
                    return;
                  }
                }
              } catch (err) {
                console.error("Error checking price line:", err);
                // Continue with normal submission if check fails
              }
            }
            
            // Normal submission (no warning needed)
            submitHour(values, helpers);
          } catch (err) {
            setIsSaving(false);
            toast.error("Something went wrong...");
            console.error(err);
            helpers.setStatus({ success: false });
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => {
          const selectedDateLocked = isLockedByPolicy(formik.values.date);
          const selectedHoliday = formik.values.date
            ? holidayByDate.get(String(formik.values.date).substring(0, 10))
            : null;

          // Helper to get current project object
          const getCurrentProject = () => {
            return (
              formik.values?.project ||
              item?.project ||
              prefill?.project ||
              (formik.values?.project_id ? { id: formik.values.project_id } : null)
            );
          };

          const getCurrentProjectActivity = () => {
            return (
              formik.values?.project_activity ||
              item?.project_activity ||
              prefill?.project_activity ||
              null
            );
          };

          const getCurrentEmployee = () => {
            const uid = formik.values.user_id;
            if (uid == null || uid === "") return null;
            if (item?.user && String(item.user.id) === String(uid)) return item.user;
            if (prefill?.user && String(prefill.user.id) === String(uid)) return prefill.user;
            if (currentUser && String(currentUser.id) === String(uid)) return currentUser;
            return { id: uid };
          };

          const currentEmploymentEnd =
            getEmploymentEndDate(getCurrentEmployee()) ||
            employmentEndByUser[formik.values.user_id] ||
            null;

          const isHourDateAllowed = (date) => {
            if (!date) return false;
            if (!canProcessHour && isLockedByPolicy(date)) return false;
            if (isDateAfterEmploymentEnd(date, { employment_ends: currentEmploymentEnd })) {
              return false;
            }
            if (absenceDateKeys.has(date)) return false;
            return true;
          };

          const loadHourDateConstraints = (month, year, uid) => {
            if (!uid) return;
            const mm = String(month).padStart(2, "0");
            const last = new Date(year, month, 0).getDate();
            const from = `${year}-${mm}-01`;
            const to = `${year}-${mm}-${String(last).padStart(2, "0")}`;
            fetchUserAbsences(uid, { date_from: from, date_too: to }).then((res) => {
              const rows = Array.isArray(res?.data)
                ? res.data
                : Array.isArray(res?.data?.data)
                  ? res.data.data
                  : [];
              setAbsenceDateKeys(expandAbsenceDateKeys(rows));
            });
            if (employmentEndByUser[uid] === undefined) {
              axios
                .get(`/users/${uid}`, {
                  params: {
                    fields: "id,employment.employment_ends,employment.end_date",
                  },
                })
                .then((res) => {
                  setEmploymentEndByUser((prev) => ({
                    ...prev,
                    [uid]: getEmploymentEndDate(res.data),
                  }));
                })
                .catch(() => {
                  setEmploymentEndByUser((prev) => ({ ...prev, [uid]: null }));
                });
            }
          };

          // Handle submit button click - validate first; only set loading in onSubmit when validation passes
          const handleSubmitClickInternal = async () => {
            if (selectedDateLocked) {
              toast.error(t("Denne datoen er låst for timeregistrering."));
              return;
            }
            if (formDisabled(formik.values.date) || isSaving || formik.isSubmitting) return;
            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) {
              formik.setErrors(errors);
              formik.setTouched(
                Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), { ...formik.touched })
              );
              if (errors.project_activity_id) {
                toast.error(t("Aktivitet er påkrevd"));
              }
              return;
            }
            formik.submitForm();
          };

          // New design: header tabs ("Ny timeføring" / "Registrerte timer")
          const designTabs = !item?.id ? (
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <button type="button" onClick={() => setMainView("form")} style={dsTabStyle(mainView === "form")}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                {t("Ny timeføring")}
              </button>
              <button type="button" onClick={() => setMainView("list")} style={dsTabStyle(mainView === "list")}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>
                {t("Registrerte timer")}
              </button>
            </div>
          ) : null;

          // New design: "Registrerte timer" day list
          const dayTotal = listEntries.reduce((s, h) => s + (parseFloat(h?.time) || 0), 0);
          const dayTotalLabel = `${Math.floor(dayTotal)}t ${pad2(Math.round((dayTotal - Math.floor(dayTotal)) * 60))}m`;
          const shiftListDate = (n) => {
            const d = new Date(`${listDate}T00:00:00`);
            d.setDate(d.getDate() + n);
            setListDate(toISODate(d));
          };
          const navBtnStyle = { width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #E2E8F0", background: "#fff", borderRadius: 11, cursor: "pointer", color: "#475569", flex: "0 0 auto" };
          const designListView = (
            <div style={{ background: "#F8FAFC", borderRadius: 16, padding: 16, marginTop: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 18 }}>
                <button type="button" onClick={() => shiftListDate(-1)} style={navBtnStyle} aria-label="Forrige dag">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                <span style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: ACCENT.solid, textTransform: "capitalize" }}>{formatLongDateNb(listDate)}</span>
                  <input type="date" value={listDate} onChange={(e) => e.target.value && setListDate(e.target.value)} style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", border: "none" }} />
                </span>
                <button type="button" onClick={() => shiftListDate(1)} style={navBtnStyle} aria-label="Neste dag">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                </button>
              </div>

              <div style={{ display: "flex", gap: 12, marginBottom: 18 }}>
                <div style={{ flex: 1, background: ACCENT.heroBg, border: `1.5px solid ${ACCENT.heroBorder}`, borderRadius: 16, padding: "14px 16px" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#64748B", marginBottom: 4 }}>{t("Sum timer")}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-.02em", color: ACCENT.deep }}>{dayTotalLabel}</div>
                </div>
                <div style={{ flex: 1, background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 16, padding: "14px 16px" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#64748B", marginBottom: 4 }}>{t("Føringer")}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-.02em", color: "#0F172A" }}>{listEntries.length}</div>
                </div>
              </div>

              {listLoading ? (
                <div style={{ textAlign: "center", padding: 32, color: "#94A3B8", fontSize: 14 }}>{t("Laster…")}</div>
              ) : listEntries.length === 0 ? (
                <div style={{ textAlign: "center", padding: "38px 20px" }}>
                  <div style={{ width: 64, height: 64, margin: "0 auto 16px", borderRadius: 18, background: ACCENT.soft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={ACCENT.solid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#0F172A", marginBottom: 6 }}>{t("Ingen timer ført")}</div>
                  <div style={{ fontSize: 14, color: "#64748B", margin: "0 auto 20px", maxWidth: 240, lineHeight: 1.5 }}>{t("Du har ikke registrert timer denne dagen ennå.")}</div>
                  <button type="button" onClick={() => setMainView("form")} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 22px", borderRadius: 13, border: "none", color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer", fontFamily: "inherit", background: ACCENT.grad, boxShadow: `0 10px 20px -8px ${ACCENT.glow}.6)` }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                    {t("Før time")}
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {listEntries.map((h) => {
                    const projName = h?.project?.displayTitle || h?.project?.name || "—";
                    const projColor = avatarColor(h?.project?.id || projName);
                    const actLabel =
                      h?.project_activity?.display_title || h?.project_activity?.displayTitle || h?.project_activity?.title || "—";
                    const tv = parseFloat(h?.time) || 0;
                    const durLabel = `${Math.floor(tv)}t ${pad2(Math.round((tv - Math.floor(tv)) * 60))}m`;
                    const emp = h?.user?.displayTitle;
                    return (
                      <button
                        type="button"
                        key={h?.id}
                        onClick={() => {
                          if (h?.id != null) {
                            setResolvedId(h.id);
                            setMainView("form");
                          }
                        }}
                        style={{ display: "flex", alignItems: "stretch", background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 16, overflow: "hidden", width: "100%", padding: 0, textAlign: "left", cursor: "pointer", fontFamily: "inherit" }}
                      >
                        <div style={{ width: 5, flex: "0 0 auto", background: projColor }} />
                        <div style={{ flex: 1, minWidth: 0, padding: "13px 14px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: "#0F172A", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{projName}</span>
                            <span style={{ fontSize: 13.5, fontWeight: 800, color: ACCENT.solid, background: ACCENT.soft, padding: "4px 10px", borderRadius: 8, whiteSpace: "nowrap", flex: "0 0 auto" }}>{durLabel}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#475569", background: "#F1F5F9", padding: "3px 9px", borderRadius: 7 }}>{actLabel}</span>
                            {(isAdmin || canProcessHour) && emp ? <span style={{ fontSize: 12.5, color: "#94A3B8", fontWeight: 600 }}>{emp}</span> : null}
                          </div>
                          {h?.description ? <div style={{ fontSize: 13, color: "#64748B", marginTop: 8, lineHeight: 1.45 }}>{h.description}</div> : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div style={{ display: "flex", position: "sticky", bottom: 0, zIndex: 5, background: "#fff", margin: "16px -16px -16px", padding: "12px 16px", borderTop: "1px solid #EEF1F5" }}>
                <button type="button" onClick={() => setMainView("form")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 16, borderRadius: 14, border: "none", color: "#fff", fontWeight: 800, fontSize: 16, cursor: "pointer", fontFamily: "inherit", background: ACCENT.grad, boxShadow: `0 12px 24px -8px ${ACCENT.glow}.6)` }}>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                  {t("Før ny time")}
                </button>
              </div>
            </div>
          );

          return (
            <div style={{ position: "relative" }}>
            {/* Design header: close (X) next to the title + selected date */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              {onClose && (
                <button
                  type="button"
                  onClick={() => onClose()}
                  aria-label={t("Lukk")}
                  style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", border: "none", background: "#F1F5F9", borderRadius: 11, cursor: "pointer", color: "#475569", flex: "0 0 auto" }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A", letterSpacing: "-.01em" }}>
                  {item?.id ? t("Rediger time") : mainView === "list" ? t("Registrerte timer") : t("Ny timeføring")}
                </div>
                <div style={{ fontSize: 12.5, color: "#94A3B8", fontWeight: 600, textTransform: "capitalize", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {formatLongDateNb(!item?.id && mainView === "list" ? listDate : formik.values.date)}
                </div>
              </div>
              {item?.id && <HourBillingButton hourIds={[item.id]} disabled={isSaving || formik.dirty} onSaved={(hours) => { setItem(hours[0]); onChange?.(hours[0]); }} />}
            </div>

            {item?.id && (
              <div
                className="mb-3 flex gap-1 border-b border-slate-200"
                role="tablist"
                aria-label={`${t("hours.form_tab_details")} / ${t("histories.title")}`}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={hourEditorTab === "details"}
                  className={
                    hourEditorTab === "details"
                      ? "rounded-t-lg border border-b-0 border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-900"
                      : "rounded-t-lg border border-transparent px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  }
                  onClick={() => setHourEditorTab("details")}
                >
                  {t("hours.form_tab_details")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={hourEditorTab === "history"}
                  className={
                    hourEditorTab === "history"
                      ? "rounded-t-lg border border-b-0 border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-900"
                      : "rounded-t-lg border border-transparent px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                  }
                  onClick={() => setHourEditorTab("history")}
                >
                  {t("histories.title")}
                </button>
              </div>
            )}

            {hourEditorTab === "history" && item?.id ? (
              <div className="mb-20 max-h-[min(70vh,640px)] overflow-y-auto pr-1">
                <HistoryModuleView variant="embedded" module="hour" module_param={item.id} />
              </div>
            ) : (!item?.id && mainView === "list") ? (
              <>
                {designTabs}
                {designListView}
              </>
            ) : (
            <Form>
              {designTabs}
              <FormikDateObserver onDateChange={onDateChange} onInternalDate={setFormDate} />
              <HoursMinutesObserver useHoursMinutesFormat={useHoursMinutesFormat} />

              {selectedDateLocked && (
                <div className="mb-4">
                  <PMNotice
                    severity="warning"
                    title={t("Låst lønnsperiode")}
                    description={t("Valgt dato tilhører en låst lønnsperiode og kan ikke redigeres her.")}
                  />
                </div>
              )}

              {selectedHoliday && !selectedDateLocked && (
                <div className="mb-4">
                  <PMNotice
                    severity="info"
                    title={t("hours.form.public_holiday_title")}
                    description={t("hours.form.public_holiday_body", { name: selectedHoliday.name })}
                  />
                </div>
              )}

              {item?.id && item?.approved && canProcessHour && (
                <div className="mb-4">
                  <PMNotice
                    severity="info"
                    title={t("hours.approved_edit_notice_title")}
                    description={t("hours.approved_edit_notice_body")}
                  />
                </div>
              )}
              
              {/* Display notice errors from backend */}
              {noticeErrors && noticeErrors.length > 0 && (
                <div className="mb-4">
                  <PMNotice
                    severity="error"
                    title={t("Validation errors")}
                    description={
                      <ul className="list-disc list-inside space-y-1 mt-2">
                        {noticeErrors.map((error, index) => {
                          // Map field names to user-friendly labels
                          const fieldLabels = {
                            date: t("Date"),
                            time: t("Time"),
                            hours: t("Timer"),
                            minutes: t("Minutter"),
                            project_id: t("Prosjekt"),
                            project_activity_id: t("Aktivitet"),
                            user_id: t("Ansatt"),
                            description: t("Beskrivelse"),
                          };
                          const fieldLabel = fieldLabels[error.field] || error.field;
                          return (
                            <li key={index} className="text-sm">
                              <strong>{fieldLabel}:</strong> {t(error.message)}
                            </li>
                          );
                        })}
                      </ul>
                    }
                  />
                </div>
              )}
              
              <div style={{ background: "#F8FAFC", borderRadius: 16, padding: 16, marginTop: 8, display: "flex", flexDirection: "column", gap: 20 }}>
                {item?.id && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" style={dsPillStyle(false)} onClick={() => handleEmptyForm(formik)}>
                      {t("New Hour")}
                    </button>
                    {(canProcessHour || (canConfirmOt && item?.agreed_overtime_requested)) && (
                      <button
                        type="button"
                        style={{ ...dsPillStyle(false), color: "#0B7A3B", borderColor: "#BBF7D0", background: "#F0FDF4" }}
                        onClick={() => setApproveDialogOpen(true)}
                      >
                        {t("Behandle time")}
                      </button>
                    )}
                    <AgreedOvertimeBadge hour={item} />
                  </div>
                )}

                {/* Ansatt — kun synlig for administratorer */}
                {isAdmin && (() => {
                  const emp = getCurrentEmployee();
                  const empName = emp?.displayTitle || emp?.name || "Velg ansatt";
                  const empDisabled = formDisabled(formik.values.date);
                  return (
                    <div>
                      <label style={dsLabelStyle}>Ansatt</label>
                      <button
                        type="button"
                        disabled={empDisabled}
                        onClick={() => setEmpPickerOpen(true)}
                        style={{ ...dsFieldCardStyle, cursor: empDisabled ? "not-allowed" : "pointer", opacity: empDisabled ? 0.6 : 1 }}
                      >
                        <span style={{ width: 40, height: 40, borderRadius: 11, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 14, background: avatarColor(emp?.id || empName) }}>
                          {initialsOf(empName)}
                        </span>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 16, fontWeight: 700, color: "#0F172A" }}>{empName}</span>
                          <span style={{ display: "block", fontSize: 12.5, color: "#94A3B8", fontWeight: 500 }}>Synlig kun for administratorer</span>
                        </span>
                        <DsChevron />
                      </button>
                      <EmployeePickerSheet
                        open={empPickerOpen}
                        onClose={() => setEmpPickerOpen(false)}
                        selectedId={formik.values.user_id}
                        onSelect={(obj) => {
                          formik.setFieldValue("user_id", obj?.id ?? null);
                        }}
                      />
                    </div>
                  );
                })()}

                {/* Varighet */}
                {(() => {
                  const dis = formDisabled(formik.values.date);
                  const hoursNum = parseInt(formik.values.hours, 10) || 0;
                  const minutesNum = parseInt(formik.values.minutes, 10) || 0;
                  const totalMin = hoursNum * 60 + minutesNum;
                  const setTotalMinutes = (tm) => {
                    const clamped = Math.max(0, Math.min(1440, Math.round(tm)));
                    const h = Math.floor(clamped / 60);
                    const m = clamped % 60;
                    formik.setFieldValue("hours", String(h));
                    formik.setFieldValue("minutes", String(m));
                    formik.setFieldValue("time", (h + m / 60).toFixed(3));
                  };
                  const stepBtn = {
                    width: 36,
                    height: 36,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    borderRadius: 9,
                    background: "#F1F5F9",
                    color: "#475569",
                    cursor: dis ? "not-allowed" : "pointer",
                  };
                  if (!useHoursMinutesFormat) {
                    return (
                      <div>
                        <label style={dsLabelStyle}>Varighet</label>
                        <div style={{ background: ACCENT.heroBg, border: `1.5px solid ${ACCENT.heroBorder}`, borderRadius: 20, padding: "18px 16px" }}>
                          <PMField name="time" label="Tid" type="number" step="0.001" min="0" {...formik} disabled={dis} />
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div>
                      <label style={dsLabelStyle}>Varighet</label>
                      <div style={{ background: ACCENT.heroBg, border: `1.5px solid ${ACCENT.heroBorder}`, borderRadius: 20, padding: "18px 16px" }}>
                        <div style={{ textAlign: "center", padding: "2px 0 14px" }}>
                          <span style={{ fontSize: 46, fontWeight: 800, letterSpacing: "-.025em", color: ACCENT.deep }}>{hoursNum}</span>
                          <span style={{ fontSize: 23, fontWeight: 700, color: ACCENT.mid, margin: "0 3px 0 1px" }}>t</span>
                          <span style={{ fontSize: 46, fontWeight: 800, letterSpacing: "-.025em", color: ACCENT.deep }}>{pad2(minutesNum)}</span>
                          <span style={{ fontSize: 23, fontWeight: 700, color: ACCENT.mid, marginLeft: 2 }}>m</span>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
                          {[0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 8].map((dh) => {
                            const active = parseFloat(formik.values.time) === dh;
                            const label = (Number.isInteger(dh) ? String(dh) : dh.toFixed(1).replace(".", ",")) + "t";
                            return (
                              <button key={dh} type="button" disabled={dis} onClick={() => setTotalMinutes(dh * 60)} style={dsPillStyle(active)}>
                                {label}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#64748B", paddingLeft: 2 }}>Timer</span>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 12, padding: 5 }}>
                              <button type="button" disabled={dis} onClick={() => setTotalMinutes(totalMin - 60)} style={stepBtn} aria-label="Minus time">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M5 12h14" /></svg>
                              </button>
                              <span style={{ fontSize: 19, fontWeight: 800, color: "#0F172A" }}>{hoursNum}</span>
                              <button type="button" disabled={dis} onClick={() => setTotalMinutes(totalMin + 60)} style={stepBtn} aria-label="Pluss time">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                              </button>
                            </div>
                          </div>
                          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#64748B", paddingLeft: 2 }}>Minutter</span>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 12, padding: 5 }}>
                              <button type="button" disabled={dis} onClick={() => setTotalMinutes(totalMin - 15)} style={stepBtn} aria-label="Minus minutter">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M5 12h14" /></svg>
                              </button>
                              <span style={{ fontSize: 19, fontWeight: 800, color: "#0F172A" }}>{pad2(minutesNum)}</span>
                              <button type="button" disabled={dis} onClick={() => setTotalMinutes(totalMin + 15)} style={stepBtn} aria-label="Pluss minutter">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Avtalt overtid — kun når firma-innstilling er på */}
                {agreedOvertimeEnabled ? (
                  <div
                    style={{
                      border: "1.5px solid #E2E8F0",
                      borderRadius: 16,
                      background: "#fff",
                      padding: "14px 16px",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                        cursor: formDisabled(formik.values.date) || item?.agreed_overtime_confirmed ? "not-allowed" : "pointer",
                        opacity: formDisabled(formik.values.date) ? 0.6 : 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(formik.values.agreed_overtime_requested)}
                        disabled={formDisabled(formik.values.date) || Boolean(item?.agreed_overtime_confirmed)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          formik.setFieldValue("agreed_overtime_requested", checked);
                          if (checked) {
                            const current =
                              formik.values.agreed_overtime_hours ||
                              formik.values.time ||
                              "";
                            formik.setFieldValue(
                              "agreed_overtime_hours",
                              current !== "" ? String(current) : String(formik.values.time || "")
                            );
                          }
                        }}
                        style={{ marginTop: 3, width: 18, height: 18 }}
                      />
                      <span>
                        <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "#0F172A" }}>
                          {t("hours.agreed_overtime.checkbox_label")}
                        </span>
                        <span style={{ display: "block", fontSize: 13, color: "#64748B", marginTop: 2 }}>
                          {t("hours.agreed_overtime.checkbox_help")}
                        </span>
                      </span>
                    </label>
                    {formik.values.agreed_overtime_requested ? (
                      <div style={{ marginTop: 12 }}>
                        <label style={dsLabelStyle}>{t("hours.agreed_overtime.hours_label")}</label>
                        <input
                          type="number"
                          min="0.25"
                          step="0.25"
                          disabled={formDisabled(formik.values.date) || Boolean(item?.agreed_overtime_confirmed)}
                          value={formik.values.agreed_overtime_hours ?? ""}
                          onChange={(e) => formik.setFieldValue("agreed_overtime_hours", e.target.value)}
                          placeholder={String(formik.values.time || "")}
                          style={{
                            ...dsFieldCardStyle,
                            width: "100%",
                            opacity:
                              formDisabled(formik.values.date) || item?.agreed_overtime_confirmed
                                ? 0.6
                                : 1,
                          }}
                        />
                        {formik.errors.agreed_overtime_hours ? (
                          <p style={{ marginTop: 6, fontSize: 12, color: "#DC2626" }}>
                            {formik.errors.agreed_overtime_hours}
                          </p>
                        ) : (
                          <p style={{ marginTop: 6, fontSize: 12, color: "#94A3B8" }}>
                            {t("hours.agreed_overtime.hours_hint")}
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {/* Dato — design chips + eksisterende PMDatePicker */}
                <div>
                  <label style={dsLabelStyle}>Dato</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    <button
                      type="button"
                      disabled={formDisabled(formik.values.date) || !isHourDateAllowed(todayISODate())}
                      style={dsPillStyle(formik.values.date === todayISODate())}
                      onClick={() => formik.setFieldValue("date", todayISODate())}
                    >
                      I dag
                    </button>
                    <button
                      type="button"
                      disabled={formDisabled(formik.values.date) || !isHourDateAllowed(yesterdayISODate())}
                      style={dsPillStyle(formik.values.date === yesterdayISODate())}
                      onClick={() => formik.setFieldValue("date", yesterdayISODate())}
                    >
                      I går
                    </button>
                  </div>
                  <PMDatePicker
                    triggerVariant="design"
                    accentColor={ACCENT.solid}
                    value={formik.values.date}
                    onChange={(e) => formik.setFieldValue("date", e?.target?.value)}
                    name="date"
                    errors={formik.errors.date}
                    touched={formik.touched.date}
                    disabled={formDisabled(formik.values.date)}
                    dayInfoMap={datePickerDayInfo}
                    validationRules={{
                      maxDate: currentEmploymentEnd || undefined,
                      isDateValid: (date) => isHourDateAllowed(date),
                    }}
                    onOpen={() => {
                      const d = formik.values.date ? new Date(formik.values.date) : new Date();
                      const month = d.getMonth() + 1;
                      const year = d.getFullYear();
                      const uid = formik.values.user_id || currentUser?.id;
                      if (uid) {
                        fetchHoursForMonth(month, year, uid).then(setDatePickerDayInfo);
                        loadHourDateConstraints(month, year, uid);
                      }
                    }}
                    onMonthChange={(month, year) => {
                      const uid = formik.values.user_id || currentUser?.id;
                      if (uid) {
                        fetchHoursForMonth(month, year, uid).then(setDatePickerDayInfo);
                        loadHourDateConstraints(month, year, uid);
                      }
                    }}
                  />
                </div>
                {/* Hidden time field for backend (always present) */}
                <input type="hidden" name="time" value={formik.values.time} />

                {renderAfterDateTime && (
                  <div className="col-span-3 sm:col-span-3">{renderAfterDateTime()}</div>
                )}

                {/* Show Report selector when editing existing hour with report */}
                {item?.id && item?.report && (
                  <div className="col-span-3 sm:col-span-3">
                    <ReportSelector
                      selected={item?.report}
                      name="report_id"
                      label="Rapport"
                      {...formik}
                      disabled={formDisabled(formik.values.date)}
                    />
                  </div>
                )}

                {/* Prosjekt — design trigger + eksisterende ProjectSelector-dialog */}
                {showProjectSelectorUi && (() => {
                  const projObj = getCurrentProject();
                  const hasProject = !!projObj?.id;
                  const projLabel =
                    projObj?.displayTitle ||
                    projObj?.name ||
                    (projObj?.project_number ? `#${projObj.project_number}` : null) ||
                    "Ingen valgt";
                  const projClient = projObj?.customer?.displayTitle || "";
                  const projDisabled = formDisabled(formik.values.date) || lockProjectField;
                  return (
                    <div>
                      <label style={dsLabelStyle}>Prosjekt</label>
                      <button
                        type="button"
                        disabled={projDisabled}
                        onClick={() => setProjPickerOpen(true)}
                        style={{ ...dsFieldCardStyle, cursor: projDisabled ? "not-allowed" : "pointer", opacity: projDisabled ? 0.6 : 1 }}
                      >
                        <span style={{ width: 13, height: 13, borderRadius: 4, flex: "0 0 auto", background: hasProject ? avatarColor(projObj?.id || projLabel) : "#CBD5E1" }} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 16, fontWeight: 600, color: hasProject ? "#0F172A" : "#94A3B8" }}>{projLabel}</span>
                          {hasProject && projClient ? (
                            <span style={{ display: "block", fontSize: 13, color: "#94A3B8", fontWeight: 500, marginTop: 1 }}>{projClient}</span>
                          ) : null}
                        </span>
                        <DsChevron />
                      </button>
                      <ProjectPickerSheet
                        open={projPickerOpen}
                        onClose={() => setProjPickerOpen(false)}
                        selectedId={formik.values.project_id}
                        onSelect={(obj) => {
                          formik.setFieldValue("project_id", obj?.id != null ? String(obj.id) : null);
                          formik.setFieldValue("project", obj || null);
                          formik.setFieldValue("project_activity_id", null);
                          formik.setFieldValue("project_activity", null);
                        }}
                      />
                      {formik.errors.project_id && (
                        <p style={{ marginTop: 6, fontSize: 12, color: "#DC2626" }}>{formik.errors.project_id}</p>
                      )}
                    </div>
                  );
                })()}

                {/* Prosjektaktivitet — design trigger + eksisterende dialog */}
                {showProjectActivityUi && (() => {
                  const projForActivity =
                    getCurrentProject() ||
                    (formik.values?.project_id ? { id: formik.values.project_id } : null);
                  const act = getCurrentProjectActivity();
                  const hasActivity = !!act?.id;
                  const actLabel =
                    act?.display_title || act?.displayTitle || act?.title ||
                    (projForActivity ? t("project_activity.select") : t("project_activity.select_project_first"));
                  const actGroup =
                    act?.group?.title ||
                    (act?.group_id && act?.group_title) ||
                    "";
                  const actDisabled = formDisabled(formik.values.date) || lockActivityField || !projForActivity;
                  return (
                    <div>
                      <label style={dsLabelStyle}>Prosjektaktivitet</label>
                      <button
                        type="button"
                        disabled={actDisabled}
                        onClick={() => { if (projForActivity) setActPickerOpen(true); }}
                        style={{
                          ...dsFieldCardStyle,
                          cursor: actDisabled ? "not-allowed" : "pointer",
                          ...(projForActivity ? {} : { border: "1.5px solid #EEF1F5", background: "#F1F5F9" }),
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>
                          {actGroup ? (
                            <span style={{ display: "block", fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "#1463C7", marginBottom: 2 }}>
                              {actGroup}
                            </span>
                          ) : null}
                          <span style={{ display: "block", fontSize: 16, fontWeight: 600, color: hasActivity ? "#0F172A" : "#94A3B8" }}>{actLabel}</span>
                        </span>
                        <DsChevron />
                      </button>
                      <ActivityPickerSheet
                        open={actPickerOpen}
                        onClose={() => setActPickerOpen(false)}
                        projectId={projForActivity?.id}
                        selectedId={formik.values.project_activity_id}
                        onSelect={(obj) => {
                          formik.setFieldValue("project_activity_id", obj?.id != null ? String(obj.id) : null);
                          formik.setFieldValue("project_activity", obj || null);
                        }}
                      />
                      {formik.errors.project_activity_id && (
                        <p style={{ marginTop: 6, fontSize: 12, color: "#DC2626" }}>{formik.errors.project_activity_id}</p>
                      )}
                    </div>
                  );
                })()}

                {/* Beskrivelse */}
                {!hideDescription && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 9 }}>
                    <label style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>Beskrivelse</label>
                    <span style={{ fontSize: 12, color: "#94A3B8", fontWeight: 600 }}>{(formik.values.description || "").length}/500</span>
                  </div>
                  <textarea
                    value={formik.values.description || ""}
                    onChange={(e) => formik.setFieldValue("description", e.target.value)}
                    maxLength={500}
                    placeholder="Hva jobbet du med?"
                    disabled={formDisabled(formik.values.date)}
                    style={{ width: "100%", minHeight: 96, resize: "vertical", padding: "14px 16px", border: "1.5px solid #E2E8F0", borderRadius: 14, fontSize: 16, color: "#0F172A", background: "#fff", outline: "none", fontFamily: "inherit", lineHeight: 1.5 }}
                  />
                </div>
                )}

                {registrationPolicy?.time_entry_documents_enabled ? (
                  <div>
                    <label style={dsLabelStyle}>{t("hour.time_documents.title")}</label>

                    {!resolvedId ? (
                      formDisabled(formik.values.date) ? (
                        <p style={{ fontSize: 12.5, color: "#94A3B8" }}>{t("hour.time_documents.blocked_lock")}</p>
                      ) : hourLinkedToReport(formik.values.report_id) || formik.values.work_order_id ? (
                        <p style={{ fontSize: 12.5, color: "#94A3B8" }}>{t("hour.time_documents.blocked_linked")}</p>
                      ) : (
                        <>
                          <input
                            ref={pendingHourDocInputRef}
                            type="file"
                            multiple
                            style={{ display: "none" }}
                            accept={pendingDocAcceptAttr}
                            disabled={formDisabled(formik.values.date)}
                            onChange={(e) => {
                              const list = e.target.files;
                              if (!list?.length) return;
                              const added = [];
                              for (const file of Array.from(list)) {
                                if (!isHourPendingDocMimeAllowed(file)) {
                                  toast.error(t("hour.time_documents.pending_file_type_denied"));
                                  continue;
                                }
                                const key = `${file.name}_${file.size}_${file.lastModified}`;
                                added.push({ key, file });
                              }
                              if (added.length > 0) {
                                setPendingHourDocuments((prev) => [...prev, ...added]);
                              }
                              e.target.value = "";
                            }}
                          />
                          <div style={{ border: "1.5px dashed #CBD5E1", borderRadius: 16, padding: 18, background: "#fff", textAlign: "center" }}>
                            <div style={{ width: 46, height: 46, margin: "0 auto 10px", borderRadius: 13, background: ACCENT.soft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ACCENT.solid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" /></svg>
                            </div>
                            <div style={{ fontSize: 13.5, color: "#64748B", lineHeight: 1.5, marginBottom: 14 }}>
                              {t("hour.time_documents.subtitle")}
                            </div>
                            <button
                              type="button"
                              disabled={formDisabled(formik.values.date)}
                              onClick={() => pendingHourDocInputRef.current?.click()}
                              style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", borderRadius: 12, border: "1.5px solid #E2E8F0", background: "#fff", color: "#334155", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}
                            >
                              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                              {t("hour.time_documents.pending_add")}
                            </button>
                          </div>
                          {pendingHourDocuments.length > 0 ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
                              {pendingHourDocuments.map((row) => (
                                <div key={row.key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 12 }}>
                                  <span style={{ width: 38, height: 38, flex: "0 0 auto", borderRadius: 10, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B" }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>
                                  </span>
                                  <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: "#0F172A", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={row.file?.name}>
                                      {row.file?.name}
                                    </span>
                                    <span style={{ display: "block", fontSize: 12.5, color: "#94A3B8", fontWeight: 500 }}>{formatBytes(row.file?.size)}</span>
                                  </span>
                                  <button
                                    type="button"
                                    aria-label={t("hour.time_documents.pending_remove")}
                                    onClick={() => setPendingHourDocuments((prev) => prev.filter((x) => x.key !== row.key))}
                                    style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", border: "none", background: "transparent", borderRadius: 8, cursor: "pointer", color: "#94A3B8", flex: "0 0 auto" }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </>
                      )
                    ) : formDisabled(formik.values.date) ? (
                      <p style={{ fontSize: 12.5, color: "#94A3B8" }}>{t("hour.time_documents.blocked_lock")}</p>
                    ) : hourLinkedToReport(formik.values.report_id) || formik.values.work_order_id ? (
                      <p style={{ fontSize: 12.5, color: "#94A3B8" }}>{t("hour.time_documents.blocked_linked")}</p>
                    ) : (
                      <div style={{ border: "1.5px solid #E2E8F0", borderRadius: 16, background: "#fff", padding: 12 }}>
                        <DocumentManagerV6
                          module="hour"
                          moduleParam={String(resolvedId)}
                          mode="compact"
                          allowCopy={false}
                          allowMove={false}
                          disabled={formDisabled(formik.values.date)}
                          title={t("hour.time_documents.title")}
                        />
                      </div>
                    )}
                  </div>
                ) : null}

                {renderAfterDescription && (
                  <div className="col-span-3 sm:col-span-3">{renderAfterDescription()}</div>
                )}

                {formik.values?.user_id && formik.values?.date && canCarAllowance && hasTripsAvailable && (
                  <>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <button
                          type="button"
                          style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", borderRadius: 12, border: "1.5px solid #E2E8F0", background: "#fff", color: "#334155", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}
                          onClick={() => setCarAllowanceDialogOpen(true)}
                        >
                          {t("Add Car Allowance")}
                        </button>

                        <div style={{ fontSize: 13, color: "#64748B", fontWeight: 600 }}>
                          {formik?.values?.trips ? formik?.values?.trips?.length : 0} Turer valgt
                        </div>
                      </div>
                    </div>

                    <CarAllowanceSelectionModal
                      hour={formik.values}
                      open={carAllowanceDialogOpen}
                      onClose={() => setCarAllowanceDialogOpen(false)}
                      onSelect={(tripIds) => formik.setFieldValue("trips", tripIds)}
                      selected={formik.values?.trips}
                      zIndex={2100}
                    />
                  </>
                )}

                <div style={{ display: "flex", gap: 12, position: "sticky", bottom: 0, zIndex: 5, background: "#fff", margin: "8px -16px -16px", padding: "12px 16px", borderTop: "1px solid #EEF1F5" }}>
                  <button
                    type="button"
                    onClick={() => onClose && onClose()}
                    style={{ padding: "16px 22px", borderRadius: 14, border: "1.5px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 700, fontSize: 16, cursor: "pointer", fontFamily: "inherit", flex: "0 0 auto" }}
                  >
                    {t("Avbryt")}
                  </button>
                  {(() => {
                    const dis = formDisabled(formik.values.date) || isSaving || formik.isSubmitting;
                    const hasTime = (parseFloat(formik.values.time) || 0) > 0;
                    const canSave =
                      hasTime && !!formik.values.project_id && !!formik.values.project_activity_id && !dis;
                    const base = {
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: 16,
                      borderRadius: 14,
                      border: "none",
                      fontFamily: "inherit",
                      fontSize: 16,
                      fontWeight: 800,
                      transition: "all .2s",
                    };
                    const style = canSave
                      ? { ...base, color: "#fff", background: ACCENT.grad, boxShadow: `0 12px 24px -8px ${ACCENT.glow}.6)`, cursor: "pointer" }
                      : { ...base, background: "#E2E8F0", color: "#94A3B8", cursor: "not-allowed", boxShadow: "none" };
                    return (
                      <button type="button" disabled={!canSave} onClick={handleSubmitClickInternal} style={style}>
                        {isSaving || formik.isSubmitting ? (
                          <span>{t("Lagrer…")}</span>
                        ) : (
                          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M20 6L9 17l-5-5" />
                            </svg>
                            {t("Lagre time")}
                          </span>
                        )}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </Form>
            )}

            {savedOverlay && (
              <div style={{ position: "fixed", inset: 0, zIndex: 40, background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center", borderRadius: 16, overflowY: "auto" }}>
                <div style={{ width: 88, height: 88, borderRadius: 999, background: ACCENT.soft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke={ACCENT.solid} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                </div>
                <h2 style={{ margin: "22px 0 6px", fontSize: 24, fontWeight: 800, color: "#0F172A" }}>{t("Time lagret")}</h2>
                <p style={{ margin: "0 0 22px", fontSize: 14.5, color: "#64748B", maxWidth: 280 }}>
                  {t("Timeføringen er registrert. Filer lastes opp automatisk.")}
                </p>
                {savedSummary && (
                  <div style={{ width: "100%", maxWidth: 330, background: "#F8FAFC", border: "1.5px solid #EEF1F5", borderRadius: 16, padding: "6px 16px", marginBottom: 24 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: "1px solid #EEF1F5" }}>
                      <span style={{ fontSize: 14, color: "#64748B", fontWeight: 600 }}>{t("Varighet")}</span>
                      <span style={{ fontSize: 15, color: "#0F172A", fontWeight: 800 }}>{savedSummary.durationLabel}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: "1px solid #EEF1F5" }}>
                      <span style={{ fontSize: 14, color: "#64748B", fontWeight: 600 }}>{t("Prosjekt")}</span>
                      <span style={{ fontSize: 15, color: "#0F172A", fontWeight: 700, textAlign: "right" }}>{savedSummary.projectLabel}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0" }}>
                      <span style={{ fontSize: 14, color: "#64748B", fontWeight: 600 }}>{t("Dato")}</span>
                      <span style={{ fontSize: 15, color: "#0F172A", fontWeight: 700, textTransform: "capitalize" }}>{savedSummary.dateLabel}</span>
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", gap: 12, width: "100%", maxWidth: 330 }}>
                  <button
                    type="button"
                    onClick={() => {
                      const saved = lastSavedHourRef.current;
                      lastSavedHourRef.current = null;
                      setSavedOverlay(false);
                      setSavedSummary(null);
                      // Notify the parent now (refreshes list / closes dialog).
                      if (onChange && saved) onChange(saved);
                      if (onClose) onClose();
                      else handleEmptyForm(formik);
                    }}
                    style={{ flex: 1, padding: 15, borderRadius: 14, border: "1.5px solid #E2E8F0", background: "#fff", color: "#475569", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}
                  >
                    {t("Lukk")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Keep the dialog open for another entry; the saved hour is
                      // already persisted and will be reported to the parent on Lukk.
                      setSavedOverlay(false);
                      setSavedSummary(null);
                      handleEmptyForm(formik);
                    }}
                    style={{ flex: 1, padding: 15, borderRadius: 14, border: "none", color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer", fontFamily: "inherit", background: ACCENT.grad, boxShadow: `0 10px 20px -8px ${ACCENT.glow}.6)` }}
                  >
                    {t("Registrer ny")}
                  </button>
                </div>
              </div>
            )}
            </div>
          );
        }}
      </Formik>

      {(canProcessHour || canConfirmOt) && (
        <ApproveHourDialog
          open={approveDialogOpen}
          hour={item}
          onClose={() => setApproveDialogOpen(false)}
          onRefresh={(data) => {
            const next = data?.data || data;
            if (next?.id) setItem((prev) => ({ ...prev, ...next }));
          }}
        />
      )}
    </>
  );
};

HourForm.propTypes = {
  projectRequired: PropTypes.bool,
  reportRequired: PropTypes.bool,
  deviationRequired: PropTypes.bool,
  activityRequired: PropTypes.bool,
  hideDescription: PropTypes.bool,
  hideAutoSelectedProject: PropTypes.bool,
  showPrefilledContext: PropTypes.bool,
};
